import React from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import {messageTime} from "utilities/helperFunctions";
import _ from "lodash";

import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import {ReactComponent as CalendarIcon} from "connectui/icons/calendar.svg";
import {ReactComponent as MoreIcon} from "connectui/icons/more-vertical.svg";
import {otaIcons} from "../../../../configuration/constants";
import StatusIcon from "../../../../connectui/component/display/StatusIcon";

import Container from "../../../../connectui/component/layout/Container";
import ProfilePicture from "../../../../connectui/component/display/ProfilePicture";
import Text from "../../../../connectui/component/display/Text";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ChipText from "connectui/component/display/chip/ChipText";
import Skeleton from "connectui/component/display/Skeleton";
import ICONS from "connectui/icons/iconMap";

const channelIcons = {
  VRBO: "vrbo-circle",
  Airbnb: "airbnb-circle",
  Expedia: "expedia-circle",
  Google: "google-circle",
  "Booking.com": "bookingcom-circle",
};

const useStyles = (theme) =>
  buildStyles({
    cardContainer: {
      height: "100px",
      width: "100%",
      cursor: "pointer",
      borderBottom: `1px solid ${theme.colors.border}`,
      position: "relative",
      overflow: "hidden",
    },
    itemContainer: {
      transition: "background-color 0.15s",
      "&:hover": {
        backgroundColor: theme.colors.gray100,
      },
    },
    profileContainer: {
      position: "relative",
      flexShrink: 0,
      "&.unread::after": {
        content: '""',
        position: "absolute",
        top: "-1px",
        left: "-1px",
        right: "-1px",
        bottom: "-1px",
        border: `1px solid ${theme.colors.primary400}`,
        borderRadius: "50%",
        zIndex: 1,
      },
    },
    otaIcon: {
      width: "22px",
      height: "22px",
      border: `1px solid ${theme.colors.white}`,
      borderRadius: "50%",
      padding: "1px",
      position: "relative",
      zIndex: 2,
    },
    nameText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    text: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
    },
    metaContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      flex: "0 0 auto",
      minWidth: "80px",
      justifyContent: "flex-end",
      right: theme.spacing(0),
    },
    statusIconContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginLeft: theme.spacing(1.5),
    },
    moreIcon: {
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      padding: theme.spacing(0.5),
      "&:hover": {
        backgroundColor: theme.colors.gray200,
      },
    },
  });

const ThreadCardComponent = ({
  guest,
  house,
  selected,
  onClick = () => null,
  openMenu,
  skeleton = {
    enabled: false,
    width: "100%",
    height: "100px",
  },
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();

  if (skeleton.enabled) {
    return (
      <FlexContainer
        gap={theme.spacing(3)}
        className={classes.cardContainer}
        sx={{backgroundColor: theme.colors.white}}
        padding={theme.spacing(2, 2, 2.5, 5)}
      >
        <Skeleton
          width={"40px"}
          height={"40px"}
          style={{borderRadius: "50%"}}
        />
        <FlexContainer flexDirection="column" gap={theme.spacing(2)} flex={1}>
          <Skeleton
            width={"72px"}
            height={"20px"}
            style={{borderRadius: `${theme.radius.lg}px`}}
          />
          <Skeleton
            width={"90%"}
            height={"12px"}
            style={{borderRadius: `${theme.radius.lg}px`}}
          />
        </FlexContainer>
      </FlexContainer>
    );
  }

  const channels = _.uniq(guest?.channels ?? []).filter(
    (c) => !!guest && !!otaIcons(guest, "1")[c],
  );
  const houseName = house?.nickname || house?.name;
  const messagePreview = guest?.message_preview ?? "";
  const isUnread = guest?.unread_messages;
  const isUnanswered = guest?.unanswered_messages;
  const hasStatusIndicator = isUnread || isUnanswered;
  const formattedMessagePreview = messagePreview.startsWith("--deleted-note_")
    ? t("inbox.threads.guest.message-preview.deleted-note")
    : messagePreview;

  const textWeight = isUnread || isUnanswered ? "semibold" : "regular";
  const textColor = isUnread ? "textPrimary" : "textSecondary";
  const nameColor =
    !isUnanswered && !isUnread ? "textSecondary" : "textPrimary";

  return (
    <FlexContainer
      onClick={onClick}
      gap={theme.spacing(3)}
      className={`${classes.cardContainer} ${classes.itemContainer}`}
      padding={theme.spacing(2, 2, 2.5, 5)}
      sx={{
        backgroundColor: selected ? theme.colors.primary50 : undefined,
      }}
    >
      <Container
        className={`${classes.profileContainer} ${isUnread ? "unread" : ""}`}
      >
        <ProfilePicture
          name={guest?.name || "?"}
          badges={channels.map((c) => (
            <SvgIcon
              key={c}
              component={ICONS[channelIcons[c]]}
              className={classes.otaIcon}
            />
          ))}
        />
      </Container>

      <FlexContainer
        flexDirection="column"
        flex={1}
        sx={{minWidth: 0}}
        height="100%"
        overflow="hidden"
      >
        <FlexContainer flexDirection="column" flex={1} width="100%">
          <FlexContainer
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            overflow="hidden"
            position="relative"
          >
            <FlexContainer
              flex="1 1 auto"
              sx={{
                minWidth: 0,
                maxWidth: "calc(100% - 100px)",
                paddingRight: theme.spacing(1),
              }}
              alignItems="center"
              overflow="hidden"
            >
              <Text
                variant={"textMD"}
                weight={textWeight}
                color={nameColor}
                className={classes.nameText}
              >
                {guest?.name ?? ""}
              </Text>
              {hasStatusIndicator && (
                <Container className={classes.statusIconContainer}>
                  <StatusIcon color={isUnread ? "primary" : "gray400"} />
                </Container>
              )}
            </FlexContainer>

            <Container className={classes.metaContainer}>
              <Text
                variant="textXS"
                weight="medium"
                color="textSecondary"
                sx={{
                  flex: "0 0 auto",
                  whiteSpace: "nowrap",
                }}
              >
                {messageTime(guest?.updated_at)}
              </Text>
              <SvgIcon
                onClick={(e) => {
                  e.stopPropagation();
                  openMenu && openMenu(e);
                }}
                component={MoreIcon}
                className={classes.moreIcon}
                sx={{color: theme.colors.textSecondary}}
              />
            </Container>
          </FlexContainer>

          <FlexContainer
            width="100%"
            overflow="hidden"
            position="relative"
            flexDirection="column"
          >
            {!!houseName && (
              <Text
                className={classes.text}
                color="textTertiary"
                variant="textXXS"
                sx={{marginTop: theme.spacing(-1)}}
              >
                {houseName}
              </Text>
            )}
            <FlexContainer
              flex={1}
              sx={{
                overflow: "hidden",
                minWidth: 0,
                maxWidth: "calc(100% - 50px)",
                paddingRight: theme.spacing(2),
              }}
            >
              <Text
                className={classes.text}
                weight={textWeight}
                color={textColor}
                variant="textXS"
              >
                {formattedMessagePreview}
              </Text>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>

        <FlexContainer width="100%" alignItems="center">
          <FlexContainer flex={1} alignItems="center">
            {guest?.range_formatted && (
              <>
                <SvgIcon
                  component={CalendarIcon}
                  sx={{
                    color: theme.colors.textSecondary,
                    width: "12px",
                    height: "12px",
                    marginRight: theme.spacing(1),
                  }}
                />
                <Text variant="textXS" weight="medium" color="textSecondary">
                  {guest?.range_formatted?.replace("to", "→")}
                </Text>
              </>
            )}
          </FlexContainer>

          {guest?.booking_status && (
            <ChipText
              text={
                <Text
                  variant="textXS"
                  weight="medium"
                  color={`${theme.colors[guest.booking_status]}700`}
                >
                  {t(guest.booking_status)}
                </Text>
              }
              sx={{
                padding: theme.spacing(0.5, 2),
                backgroundColor:
                  theme.colors[`${theme.colors[guest.booking_status]}50`],
                "&:hover": {
                  backgroundColor:
                    theme.colors[`${theme.colors[guest.booking_status]}50`],
                },
              }}
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
};

ThreadCardComponent.propTypes = {
  guest: PropTypes.shape({
    name: PropTypes.string,
    message_preview: PropTypes.string,
    range_formatted: PropTypes.string,
    booking_status: PropTypes.string,
    unread_messages: PropTypes.bool,
    unanswered_messages: PropTypes.bool,
    updated_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    channels: PropTypes.array,
  }),
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  openMenu: PropTypes.func,
  skeleton: PropTypes.shape({
    enabled: PropTypes.bool,
    width: PropTypes.string,
    height: PropTypes.string,
  }),
};

export default ThreadCardComponent;
